import { addMinutes, isBefore, isEqual } from 'date-fns';
import { capitalize } from '@mui/material';
import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';
import {
  Appointments,
  AppointmentStatus,
  AppointmentType,
  Payments,
  ReferralSources,
} from '@prisma/client';
import { NavigateFunction } from 'react-router-dom';
import {
  Appointment,
  GeneralRequest,
  LoadedUser,
  Option,
  Section,
  Service,
} from '../../interfaces';
import {
  convertMinsToHrsMins,
  convertSnakeToTitleCase,
  getCleanedPhone,
  isEndDateAcrossDateline,
  isMinor,
} from '../../utils/global/helper';
import {
  DEFAULT_TATTOO_SERVICE_ID,
  DEFAULT_TIMEZONE,
  FIFTEEN_MIN_LENGTH_OPTIONS,
  LENGTH_OPTIONS,
  MAX_APPOINTMENT_LENGTH_IN_MINUTES,
  USE_DEFAULT_TIMEZONE_FOR_DATELINE_CHECK,
} from '../../constants/appointments';
import {
  AdditionalAppointment,
  CreateAptValues,
} from '../../utils/form/appointmentForm';
import {
  getPaymentsTipAmount,
  getPaymentsTotalDiscountedAmount,
} from '../payments';
import { Payment } from '../../types/reports';
import { GetWaiversData } from '../../types/waivers';
import {
  CustomerDetails,
  SelectedAppointment,
  ServiceWithHourlyPrice,
  TakenSessionSlot,
} from '../../types/appointments';
import {
  ALLOWED_VOID_PAYMENT_METHODS,
  CREDIT_PAYMENT_METHODS,
} from '../../constants/payments';
import type { createAppointmentChat } from '../../services/appointments';
import { ROUTES } from '../../constants/global';
import { getDefaultWaiver, getServiceWaiver } from '../waivers';
import { getRefImagesFromFlashDesign } from '../flashbooks';
import { QueueAppointment } from '../../types/queue';

/**
 * Detaches the appointment from a checkout, leaving a retail-only sale. Shared
 * so the appointment section and the line-item card remove it the same way.
 */
export function clearCheckoutAppointment({
  paymentMethod,
  setFieldValue,
  onSelectAppointment,
}: {
  paymentMethod: string | undefined;
  setFieldValue: (field: string, value: any) => void;
  onSelectAppointment: (appointment: null) => void;
}) {
  setFieldValue('appointmentId', '');
  setFieldValue('employeeId', '');
  setFieldValue('price', 0);
  // FREE only means anything against an appointment.
  if (paymentMethod === 'FREE') {
    setFieldValue('paymentMethod', '');
  }
  onSelectAppointment(null);
}

export function isCheckoutProviderChangeLocked(
  appointment:
    | Pick<SelectedAppointment, 'employeeId' | 'status'>
    | null
    | undefined,
  employeeId: string | null | undefined,
  user: Pick<LoadedUser, 'business' | 'employee'> | null | undefined,
): boolean {
  return (
    (!!appointment?.employeeId && appointment.status !== 'QUEUED') ||
    (user?.business?.type === 'INDEPENDENT' &&
      !!employeeId &&
      employeeId === user?.employee?.employeeId)
  );
}

export function isAppointmentDepositPaid(
  appointment:
    | Pick<
        Appointment | Appointments,
        'depositPrice' | 'confirmedOn' | 'depositPaidOn' | 'paymentStatus'
      >
    | null
    | undefined,
) {
  return (
    !!appointment?.depositPrice &&
    (!!appointment?.confirmedOn || !!appointment?.depositPaidOn) &&
    appointment?.paymentStatus === 'SUCCESS'
  );
}

export type AppointmentStatusKey =
  | 'unconfirmed'
  | 'confirmed'
  | 'completed'
  | 'canceled'
  | 'noShow';

// Single source of truth for status-driven visuals — derive from this
// instead of re-deriving from the raw fields elsewhere.
export function getAppointmentStatusKey(
  appointment:
    | Pick<
        Appointment | Appointments,
        | 'status'
        | 'wasNoShow'
        | 'checkoutSessionCompleted'
        | 'depositPrice'
        | 'confirmedOn'
        | 'depositPaidOn'
        | 'paymentStatus'
      >
    | null
    | undefined,
): AppointmentStatusKey {
  if (appointment?.status === 'CANCELED') return 'canceled';
  if (appointment?.wasNoShow) return 'noShow';
  if (appointment?.checkoutSessionCompleted) return 'completed';
  if (
    appointment?.status === 'CONFIRMED' ||
    isAppointmentDepositPaid(appointment)
  ) {
    return 'confirmed';
  }
  return 'unconfirmed';
}

export function isAppointmentDepositTerminalCheckoutLoading(
  appointment:
    | Pick<
        Appointment | Appointments,
        | 'depositCheckoutSessionActive'
        | 'paymentMethod'
        | 'depositPrice'
        | 'confirmedOn'
        | 'depositPaidOn'
        | 'paymentStatus'
        | 'checkoutSessionActive'
        | 'checkoutSessionCompleted'
      >
    | null
    | undefined,
) {
  return (
    !!appointment?.depositCheckoutSessionActive &&
    !isAppointmentDepositPaid(appointment) &&
    !appointment?.checkoutSessionActive &&
    !appointment?.checkoutSessionCompleted
  );
}

export function getAppointmentTipAmount(
  appointmentPayments: (Payments | Payment)[],
) {
  return getPaymentsTipAmount(appointmentPayments);
}

export function getAppointmentTotalDiscountedAmount(
  appointmentPayments: Payments[],
) {
  return getPaymentsTotalDiscountedAmount(
    appointmentPayments?.filter(payment => payment.status === 'SUCCESS'),
  );
}

export function doesCashAppointmentHaveTransaction(
  appointment: Appointment,
  payments: Payments[],
) {
  return payments.some(
    payment =>
      payment.appointmentId === appointment.appointmentId &&
      payment.method === 'CASH',
  );
}

export function filterCashAppointmentsWithoutTransactions(
  appointments: Appointment[],
  payments: Payments[],
) {
  return appointments.filter(
    appointment => !doesCashAppointmentHaveTransaction(appointment, payments),
  );
}

export function getCrossDatelineError({
  startDateTime,
  endDateTime,
  type = 'appointment',
  appointment,
  timezone,
  additionalAppointments,
}: {
  startDateTime: string | Date;
  endDateTime: string | Date;
  timezone: string;
  type?: 'appointment' | 'timeblock';
  appointment?: Pick<
    SelectedAppointment | Appointments,
    'startDateTime' | 'endDateTime'
  > | null;
  additionalAppointments?: AdditionalAppointment[];
}) {
  const label = type === 'appointment' ? 'appointment' : 'time block';
  const pluralLabel = `${label}s`;

  const isTimeChanged =
    (type === 'appointment' && !!additionalAppointments?.length) ||
    !appointment?.startDateTime ||
    !appointment?.endDateTime ||
    !isEqual(new Date(appointment.startDateTime), new Date(startDateTime)) ||
    !isEqual(new Date(appointment.endDateTime), new Date(endDateTime));

  const timezoneToCompare = USE_DEFAULT_TIMEZONE_FOR_DATELINE_CHECK
    ? DEFAULT_TIMEZONE
    : timezone;

  const appointmentsThatCross = [
    {
      startDateTime,
      endDateTime,
    },
    ...(additionalAppointments || []),
  ]
    .map(appt => {
      const start = new Date(appt.startDateTime);
      const end = new Date(appt.endDateTime);

      // Convert the start time to Pacific time to find the correct cutoff
      const startInDefaultTZ = formatInTimeZone(
        start,
        timezoneToCompare,
        'yyyy-MM-dd',
      );
      const elevenThirtyPMDefaultTZ = zonedTimeToUtc(
        `${startInDefaultTZ}T23:30:00`,
        timezoneToCompare,
      );

      const startDay = formatInTimeZone(start, timezone, 'MMM d');
      const endTime = formatInTimeZone(
        elevenThirtyPMDefaultTZ,
        timezone,
        'h:mm a',
      );

      return {
        start,
        end,
        startDay,
        endTime,
      };
    })
    .filter(appt => isEndDateAcrossDateline(appt.start, appt.end, timezone));

  if (isTimeChanged && appointmentsThatCross.length) {
    const isTzDefault = timezone === timezoneToCompare;
    const isMultiple = appointmentsThatCross.length > 1;
    const first = appointmentsThatCross[0];
    const title = `You've selected an end time of ${formatInTimeZone(
      first.end,
      timezone,
      'h:mm a',
    )}`;
    const message = isMultiple
      ? `These ${appointmentsThatCross.length} ${pluralLabel} will be scheduled to end at ${first.endTime} if you continue.`
      : `This ${label} will be scheduled from ${formatInTimeZone(
          first.start,
          timezone,
          'MMM d h:mm a',
        )} to ${first.startDay} ${first.endTime} if you continue.`;
    return {
      title,
      message: `${capitalize(pluralLabel)} that go past midnight${
        USE_DEFAULT_TIMEZONE_FOR_DATELINE_CHECK && !isTzDefault
          ? ` in ${DEFAULT_TIMEZONE} `
          : ''
      } can not be scheduled. Are you sure you want to continue? ${message}`,
    };
  }

  return null;
}

export function validatePrice(
  values: CreateAptValues,
  appointmentType: AppointmentType,
) {
  if (appointmentType === 'CONSULTATION' || appointmentType === 'WALK_IN')
    return null;

  const { priceType, minPrice, maxPrice, price, depositPrice } = values;

  if (priceType === 'FIXED' && depositPrice && depositPrice > (price ?? 0)) {
    return {
      error:
        'Deposit amount cannot be greater than price. The price of the appointment should include the deposit price.',
    };
  }

  if (priceType === 'RANGE' && (minPrice ?? 0) > (maxPrice ?? 0)) {
    return {
      error: 'Minimum price cannot be greater than maximum price.',
    };
  }
  return null;
}

export function getWalkInStartDateTime() {
  return addMinutes(new Date(), 2);
}

export function getAppointmentFormErrorMessage(error: Error) {
  if (error.message.startsWith('All stations at the shop are already booked')) {
    return error.message;
  }
  return error.message;
}

export function getCalendarTimezone(
  user: LoadedUser | undefined,
  businessTripId?: string,
) {
  return (
    user?.business.businessTrips.find(t => t.tripId === businessTripId)
      ?.timezone ||
    user?.timezone ||
    DEFAULT_TIMEZONE
  );
}

export function isAppointmentUpcoming(
  appointment: Pick<Appointments, 'endDateTime'>,
) {
  return (
    !!appointment.endDateTime &&
    isBefore(new Date(), new Date(appointment.endDateTime))
  );
}

export function isAppointmentInPast(
  appointment: Pick<Appointments, 'endDateTime'>,
) {
  return (
    !!appointment.endDateTime &&
    isBefore(new Date(appointment.endDateTime), new Date())
  );
}

export function isAppointmentCompleted(
  appointment: Pick<Appointments, 'checkoutSessionCompleted' | 'wasNoShow'>,
) {
  return appointment.checkoutSessionCompleted || appointment.wasNoShow;
}

export function isAppointmentPending(
  appointment: Pick<Appointments, 'status'>,
) {
  return (
    appointment.status === 'REQUESTED' || appointment.status === 'ACCEPTED'
  );
}

export function getServiceName(
  appointment: Pick<SelectedAppointment, 'serviceCategory' | 'serviceId'>,
  loadedUser: LoadedUser | null,
) {
  const isTattoo = appointment.serviceCategory === 'TATTOO';

  if (isTattoo) return 'Tattoo Appointment';

  const service = loadedUser?.business.services?.find(
    tempService => tempService.serviceId === appointment.serviceId,
  );

  return service?.serviceName || '';
}

function getDurationLabel(minutes: number) {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (hours > 0 && remainingMinutes > 0) {
    return `${hours} hour${
      hours > 1 ? 's' : ''
    } and ${remainingMinutes} minutes`;
  }
  if (hours > 0) {
    return `${hours} hour${hours > 1 ? 's' : ''}`;
  }
  return `${remainingMinutes} minutes`;
}

export function getServiceLengthOptions(
  service: Service,
  lengthOptions: Option<number>[],
) {
  const defaultDuration = service.defaultDurationInMinutes;
  const durations = [...lengthOptions];

  if (!defaultDuration || defaultDuration > MAX_APPOINTMENT_LENGTH_IN_MINUTES)
    return durations;

  const exists = durations.some(duration => duration.value === defaultDuration);

  if (exists) return durations;

  const defaultDurationLabel = getDurationLabel(defaultDuration);
  const defaultDurationObject = {
    label: defaultDurationLabel,
    value: defaultDuration,
  };

  durations.push(defaultDurationObject);
  durations.sort((a, b) => a.value - b.value);

  return durations;
}

export function getAppointmentStatus(appointment: {
  status: AppointmentStatus | null | undefined;
  previousStatus: AppointmentStatus | null | undefined;
}) {
  return appointment?.status === 'ARCHIVED'
    ? appointment?.previousStatus || 'ARCHIVED'
    : appointment?.status;
}

export function isWaiverSelectable({
  waivers,
  hasWaiversFeature,
  appointmentType,
  serviceId,
}: {
  waivers: GetWaiversData[] | undefined;
  hasWaiversFeature: boolean;
  appointmentType: AppointmentType | undefined;
  serviceId: number | null | undefined;
}) {
  if (!hasWaiversFeature || appointmentType === 'CONSULTATION') return false;

  if (serviceId) {
    return !!getServiceWaiver({
      waivers,
      serviceId,
      status: 'PUBLISHED',
    });
  }

  return !!getDefaultWaiver({
    waivers,
    status: 'PUBLISHED',
  });
}

export function getDepositPlacementSelectInput({
  name = 'depositPlacement',
  disabled = false,
  required = false,
}: {
  name?: string;
  disabled?: boolean;
  required?: boolean;
} = {}) {
  return {
    label: `Deposit Handling${required ? ' *' : ''}`,
    type: 'select',
    options: [
      {
        value: 'FIRST',
        label: 'Collect & apply deposit to the first appointment',
      },
      {
        value: 'LAST',
        label: 'Collect & apply deposit to the last appointment',
      },
    ],
    placeholder: 'How do you want to apply the deposit for these appointments?',
    name,
    disabled,
  };
}

export const getTakenSessionSlots = (
  index: number,
  values: Pick<
    CreateAptValues,
    'startDateTime' | 'appointmentLengthInMinutes' | 'additionalAppointments'
  >,
) => {
  // if index is -1, return all of the `additionalAppointments`
  // otherwise, return the original appointment and the `additionalAppointments` excluding the one at index
  const additionalAppointments: TakenSessionSlot[] = [
    ...(values.additionalAppointments || []),
  ].filter(s => s.startDateTime && s.appointmentLengthInMinutes);
  if (index === -1) return additionalAppointments;
  additionalAppointments.splice(index, 1);
  if (values.startDateTime && values.appointmentLengthInMinutes) {
    const startDateTime = new Date(values.startDateTime);
    const appointmentLengthInMinutes = parseInt(
      values.appointmentLengthInMinutes,
      10,
    );
    if (appointmentLengthInMinutes) {
      const endDateTime = addMinutes(startDateTime, appointmentLengthInMinutes);
      additionalAppointments.push({
        startDateTime: startDateTime.toISOString(),
        endDateTime: endDateTime.toISOString(),
      });
    }
  }
  return additionalAppointments.map(slot => ({
    startDateTime: new Date(slot.startDateTime).toISOString(),
    endDateTime: new Date(slot.endDateTime).toISOString(),
  }));
};

export const updateSessionStart = (
  additionalAppointments: AdditionalAppointment[],
  index: number,
  value: Date,
  fallbackLengthInMinutes?: string,
): AdditionalAppointment[] | null => {
  const session = additionalAppointments[index];
  if (!session) return null;
  const length = parseInt(
    session.appointmentLengthInMinutes || fallbackLengthInMinutes || '0',
    10,
  );
  const next = [...additionalAppointments];
  next[index] = {
    ...session,
    startDateTime: value.toISOString(),
    endDateTime: addMinutes(value, length).toISOString(),
  };
  return next;
};

export const isDepositPlacementRequired = (
  additionalAppointments: AdditionalAppointment[],
  depositPrice: number | undefined,
) => !!additionalAppointments?.length && (depositPrice ?? 0) > 0;

export const getCancelAppointmentDepositOptions = (
  appointment: Appointment | Appointments,
): Required<Option<string>>[] => {
  const isRefundable =
    !!appointment.depositPaymentMethod &&
    CREDIT_PAYMENT_METHODS.includes(appointment.depositPaymentMethod);
  const isVoidable =
    !!appointment.depositPaymentMethod &&
    ALLOWED_VOID_PAYMENT_METHODS.includes(appointment.depositPaymentMethod);

  const options = [
    {
      label: 'Credit the deposit to the clients account',
      value: 'UNUSED',
    },
    {
      label: 'Forfeit the deposit',
      value: 'FORFEITED',
    },
  ];

  const refundOption = {
    value: isRefundable ? 'REFUNDED' : isVoidable ? 'VOIDED' : '',
    label: isRefundable
      ? 'Refund the deposit'
      : isVoidable
      ? 'Void the deposit'
      : '',
  };

  if (refundOption.value) {
    options.splice(1, 0, refundOption);
  }

  return options;
};

export function navigateToChat(
  navigate: NavigateFunction,
  chatResponse: Awaited<ReturnType<typeof createAppointmentChat>>,
) {
  const { conversation, appointmentId, generalRequestId } = chatResponse;
  const searchParams = new URLSearchParams();
  if (appointmentId) searchParams.set('appointment', appointmentId);
  if (generalRequestId) searchParams.set('request', generalRequestId);
  const query = searchParams.toString();
  navigate(`${ROUTES.chat}/${conversation.id}${query ? `?${query}` : ''}`);
}

const FIFTEEN_MIN_BUSINESS_IDS = [
  'cm51byiny0043ntqghi8j4rwm',
  'cm4jjdgzb005yiaanc6quzb32',
  'cm33kw3wl01gf1k0za6d87mq6',
  'cm3xsygqa0076ybmqfv1ozx4b',
  'cm01dmawi009uyhu4jbmdiqo2',
  'cmgd2xq0y0px2aafa5l3yyqi7',
  'cmhz2mumk0460g6yvi2al2n95',
  'cmu68tk1w01nfe30udh3hfn0z',
  'cmgd2xq0y0px2aafa5l3yyqi7',
];

export function getLengthOptions({
  user,
  appointmentType,
  serviceId,
  currentLength,
  businessId,
}: {
  user: LoadedUser | null;
  appointmentType?: AppointmentType;
  serviceId?: number;
  // Off-list value to include as a custom option (e.g. from a calendar drag).
  currentLength?: number;
  // Overrides the user's home business (org location moves).
  businessId?: string;
}) {
  const isConsultation = appointmentType === 'CONSULTATION';

  const service = serviceId
    ? user?.business.services?.find(s => s.serviceId === serviceId)
    : null;

  const useFifteenMinLengthOptions =
    FIFTEEN_MIN_BUSINESS_IDS.includes(businessId || user?.business.id || '') ||
    user?.userId === 'cljego2px0004s60umrsops0s';

  const newAppointmentLengthOptions = useFifteenMinLengthOptions
    ? FIFTEEN_MIN_LENGTH_OPTIONS
    : LENGTH_OPTIONS.NEW_APPOINTMENT!;

  const appointmentLengthOptions = isConsultation
    ? LENGTH_OPTIONS.CONSULTATION
    : service
    ? getServiceLengthOptions(service, newAppointmentLengthOptions)
    : newAppointmentLengthOptions;

  if (
    currentLength &&
    appointmentLengthOptions &&
    !appointmentLengthOptions.some(o => Number(o.value) === currentLength)
  ) {
    return [
      ...appointmentLengthOptions,
      { label: convertMinsToHrsMins(currentLength), value: currentLength },
    ].sort((a, b) => Number(a.value) - Number(b.value));
  }

  return appointmentLengthOptions;
}

export function createReferralSourceSection(
  referralSource: ReferralSources | null,
): Section | null {
  if (!referralSource) return null;

  const rows = [
    {
      type: 'TEXT',
      label: 'Source',
      value: [referralSource.type || ''],
    },
  ];

  if (referralSource.details) {
    rows.push({
      type: 'TEXT',
      label: 'Details',
      value: [referralSource.details || ''],
    });
  }

  return {
    name: 'Referral Source',
    rows,
  } as Section;
}

export function getAppointmentFormValues(
  appointment: Appointment,
  loadedUser: LoadedUser | null,
) {
  const service = loadedUser?.business.services?.find(
    s => s.serviceId === appointment.serviceId,
  );
  const piercingLocations = service?.piercingLocations ?? [];
  const selectedPiercingLocation = piercingLocations.find(
    location => location.locationId === appointment.piercingLocationId,
  );

  const isEmployeeStillActive =
    !appointment.employeeId ||
    !appointment.businessId ||
    appointment.businessId !== loadedUser?.business?.id ||
    !!loadedUser?.business?.employees?.some(
      e => e.employeeId === appointment.employeeId,
    );

  return {
    customerId: appointment.customerId,
    firstName: appointment.customer.firstName,
    lastName: appointment.customer.lastName,
    email: appointment.customer.email,
    phoneNumber: appointment.customer.phoneNumber,
    role: appointment.customer.role,
    appointmentLengthInMinutes: appointment.appointmentLengthInMinutes
      ? String(appointment.appointmentLengthInMinutes)
      : '',
    businessId: appointment.businessId,
    dob: appointment.customer.dob,
    employeeId: isEmployeeStillActive ? appointment.employeeId : '',
    serviceId: service ? String(service.serviceId) : '0',
    tattooLocation: appointment.tattooLocation || '',
    bodySide: appointment.bodySide || '',
    tattooSize: appointment.tattooSize || '',
    tattooColor: appointment.tattooColor || '',
    tattooDetails: appointment.tattooDetails || '',
    piercingLocationId: selectedPiercingLocation
      ? String(selectedPiercingLocation.locationId)
      : '',
    locationCategory: selectedPiercingLocation
      ? selectedPiercingLocation.locationCategory
      : '',
    priceType: appointment.priceType,
    price: appointment.price || 0,
    maxPrice: appointment.maxPrice,
    minPrice: appointment.minPrice,
    hourlyPrice: appointment.hourlyPrice,
    depositPrice: appointment.depositPrice || 0,
    businessTripId: appointment.businessTripId || '',
    internalNotes: appointment.internalNotes,
    newImages: appointment.refImgsId?.map(url => ({
      id: url,
      url,
    })),
    customerGuestId:
      appointment.customerGuest?.id ?? appointment.customerGuestId ?? undefined,
  } as Partial<CreateAptValues>;
}

export function isDepositAllowed(
  appointment: Pick<Appointment | Appointments, 'appointmentType'>,
) {
  return (
    appointment.appointmentType !== 'CONSULTATION' &&
    appointment.appointmentType !== 'WALK_IN'
  );
}

export function getSelectedArtistsText(
  user: LoadedUser | null | undefined,
  selectedArtists: string[],
) {
  return selectedArtists
    .map(employeeId =>
      employeeId === 'first-available'
        ? 'First Available'
        : user?.business?.employees?.find(emp => emp.employeeId === employeeId)
            ?.displayName,
    )
    .join(', ');
}

export function getAllRefImages(appointment: Appointment | GeneralRequest) {
  const refImages = [...(appointment.refImgsId ?? [])];

  if (!('flashDesign' in appointment) || !appointment.flashDesign)
    return refImages;

  const flashDesignImages = getRefImagesFromFlashDesign(
    appointment.flashDesign,
  );

  return [...flashDesignImages, ...refImages];
}

export function getCustomerDetails(
  appointment: Appointment | GeneralRequest | QueueAppointment,
): CustomerDetails {
  const customerDob = appointment.customer.dob
    ? new Date(appointment.customer.dob).toISOString().split('T')[0]
    : undefined;
  const customerPhoneNumber = getCleanedPhone(appointment.customer.phoneNumber);

  if (appointment.customerGuest) {
    const guestDob = appointment.customerGuest.dob
      ? new Date(appointment.customerGuest.dob).toISOString().split('T')[0]
      : undefined;
    const guestIsMinor = isMinor(
      guestDob,
      'startDateTime' in appointment ? appointment.startDateTime : undefined,
    );
    const relation = appointment.customerGuest.relation ?? 'OTHER';
    return {
      firstName: appointment.customerGuest.firstName || '',
      lastName: appointment.customerGuest.lastName || '',
      dob: guestDob,
      phoneNumber: undefined,
      email: appointment.customerGuest.email || undefined,
      primaryCustomer: {
        firstName: appointment.customer.firstName || '',
        lastName: appointment.customer.lastName || '',
        dob: customerDob,
        phoneNumber: customerPhoneNumber,
        email: appointment.customer.email || '',
        relation,
        label: guestIsMinor
          ? convertSnakeToTitleCase(
              relation === 'OTHER' ? 'GUARDIAN' : relation,
            )
          : 'Is a guest of',
      },
    };
  }

  if (appointment.isForSomeoneElse) {
    const recipientDob = appointment.recipientDob
      ? new Date(appointment.recipientDob).toISOString().split('T')[0]
      : undefined;
    const recipientIsMinor = isMinor(
      recipientDob,
      'startDateTime' in appointment ? appointment.startDateTime : undefined,
    );
    const recipientRelation = appointment.recipientUserRelation ?? 'OTHER';
    return {
      firstName: appointment.recipientFirstName || '',
      lastName: appointment.recipientLastName || '',
      dob: recipientDob,
      phoneNumber: undefined,
      email: undefined,
      ...(recipientIsMinor && {
        primaryCustomer: {
          firstName: appointment.customer.firstName || '',
          lastName: appointment.customer.lastName || '',
          dob: customerDob,
          phoneNumber: customerPhoneNumber,
          email: appointment.customer.email || '',
          relation: recipientRelation,
          label: convertSnakeToTitleCase(
            recipientRelation === 'OTHER' ? 'GUARDIAN' : recipientRelation,
          ),
        },
      }),
    };
  }

  return {
    firstName: appointment.customer.firstName || '',
    lastName: appointment.customer.lastName || '',
    dob: customerDob,
    phoneNumber: customerPhoneNumber,
    email: appointment.customer.email || '',
  };
}

/** Synthetic "default" tattoo service (null serviceId). Uses business default tattoo deposit as price when provided.
 *  When the relevant employee has a default hourly rate, sets `defaultHourlyPrice` so callers can detect variable pricing. */
export function getDefaultTattooService(
  defaultTattooDeposit?: number | null,
  employeeDefaultHourlyRate?: number | null,
): ServiceWithHourlyPrice {
  const price = defaultTattooDeposit ?? 0;
  return {
    serviceId: DEFAULT_TATTOO_SERVICE_ID,
    serviceName: 'Tattoo',
    serviceCategory: 'TATTOO',
    serviceDescription: '',
    defaultDurationInMinutes: 60,
    defaultPrice: price,
    defaultHourlyPrice: employeeDefaultHourlyRate ?? undefined,
    defaultDepositPrice: price || null,
    enableDirectBookings: true,
    minimumAgeInMonths: 0,
    employeesToServices: [],
  };
}

/** Group services by category; tattoo first. Excludes archived/deleted services. */
export function groupServicesByCategory(
  services: Service[] | undefined,
  defaultTattooDeposit?: number | null,
  employeeDefaultHourlyRate?: number | null,
): Map<string, ServiceWithHourlyPrice[]> {
  const map = new Map<string, Service[]>();
  const activeServices =
    services?.filter(
      s => !(s as Service & { archivedAt?: Date | null }).archivedAt,
    ) ?? [];
  const tattooServices = activeServices.filter(
    s => s.serviceCategory === 'TATTOO',
  );
  const defaultTattoo = getDefaultTattooService(
    defaultTattooDeposit,
    employeeDefaultHourlyRate,
  );

  const tattooList = [...tattooServices];
  if (!tattooList.some(s => s.serviceId === DEFAULT_TATTOO_SERVICE_ID)) {
    tattooList.unshift(defaultTattoo);
  }
  if (tattooList.length > 0) {
    map.set('TATTOO', tattooList);
  }

  const otherCategories = [
    ...new Set(activeServices.map(s => s.serviceCategory)),
  ].filter(c => c !== 'TATTOO');
  otherCategories.sort((a, b) => a.localeCompare(b));
  otherCategories.forEach(cat => {
    const list = activeServices.filter(s => s.serviceCategory === cat);
    if (list.length > 0) map.set(cat, list);
  });

  if (map.size === 0) {
    map.set('TATTOO', [defaultTattoo]);
  }

  return map;
}
